import React from 'react';
import { Row } from '../settingsPrimitives';

/**
 * "Install MCP Server" (**D9**) — one state, rendered honestly.
 *
 * The button ships now and **cannot succeed on any build that exists before
 * the first release that publishes bridges**: the URL is
 * `cdn.apiboo.com/<this app's version>/apiboo-mcp-bridge-<triple>`, and the
 * digest it is checked against is compiled into this build. Until such a
 * release exists, `mcp_bridge_status` returns `unavailable_build` and this
 * renders a disabled button with a plain sentence saying so — it must not look
 * broken, and it must not pretend to work.
 *
 * **O5's "no annoying warnings" is in force on this whole section**: no
 * banner, no `⚠`, no coloured strip, including on the failure states, which
 * are plain body copy in the normal description style. Every failure names a
 * REASON from Rust's fixed vocabulary rather than a bare "failed".
 */
const ERROR_KEYS = {
  bridge_checksum_mismatch: 'mcp.bridgeErrChecksum',
  bridge_download_failed: 'mcp.bridgeErrDownload',
  bridge_not_published_for_this_version: 'mcp.bridgeErrNotPublished',
  bridge_cannot_verify: 'mcp.bridgeErrCannotVerify',
  bridge_in_use: 'mcp.bridgeErrInUse',
  bridge_unsupported_platform: 'mcp.bridgeUnsupported',
};

export function errorKeyFor(code) {
  return ERROR_KEYS[code] || 'mcp.bridgeErrDownload';
}

export default function McpBridgeInstall({ t, status, busy, error, onInstall }) {
  if (!status) return null;

  // No helper program exists for this platform: no button at all, and one
  // sentence saying the HTTP transport is the only one here.
  if (status.state === 'unsupported') {
    return (
      <Row label={t('mcp.bridgeTitle')} desc={t('mcp.bridgeUnsupported')}>
        <span className="settings-static-value">—</span>
      </Row>
    );
  }

  const unavailable = status.state === 'unavailable_build';
  const installed = status.state === 'installed';
  const updatable = status.state === 'update_available';

  let label = t('mcp.bridgeInstall');
  if (busy) label = t('mcp.bridgeInstalling');
  else if (error) label = t('mcp.bridgeTryAgain');
  else if (installed) label = t('mcp.bridgeReinstall');
  else if (updatable) label = t('mcp.bridgeUpdate');

  let desc = t('mcp.bridgeDesc');
  if (unavailable) desc = t('mcp.bridgeUnavailableBuild');
  else if (error) desc = t(errorKeyFor(error));
  else if (installed) desc = t('mcp.bridgeInstalled', { version: status.installedVersion || '' });
  else if (updatable) {
    desc = t('mcp.bridgeUpdateAvailable', {
      installed: status.installedVersion || '',
      current: status.appVersion || '',
    });
  }

  return (
    <>
      <Row label={t('mcp.bridgeTitle')} desc={desc}>
        <button
          type="button"
          className="settings-action-btn"
          disabled={busy || unavailable}
          onClick={onInstall}
        >
          {label}
        </button>
      </Row>
      {(installed || updatable) && status.path && (
        <Row label={t('mcp.bridgePath')} desc={<code className="mcp-path">{status.path}</code>}>
          <span className="settings-static-value" />
        </Row>
      )}
    </>
  );
}
